import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import React, { useEffect, useRef, useState } from 'react';
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js';
import { verifyCard } from 'src/app/services/ClientService';
import { useTranslation } from 'react-i18next';
import { InputText } from 'primereact/inputtext';
import { Card } from 'primereact/card';
import { Checkbox } from 'primereact/checkbox';
import { useNavigate } from 'react-router-dom';
import { set } from 'date-fns';
import { getPlanSupportedCountries } from 'src/app/services/PlanService';
import { AxiosResponse } from 'axios';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';

const elementStyle = {
  style: {
    base: {
      fontSize: '14px',
      color: '#495057',
      '::placeholder': { color: '#9ca3af' },
    },
    invalid: {
      color: '#e24c4c',
    },
  },
};

interface DialogProps {
  visible: boolean;
  onClose: () => void;
  onVerificationComplete: (skipConfirmRedemption?: boolean) => void;
  header?: string;
  cardNumber: string | null;
  clientSecret: string; // ✅ ADD THIS
  paymentIntentId: string; // ✅ ADD THIS
}

const VerifyingCardDialog: React.FC<DialogProps> = ({
  visible,
  onClose,
  onVerificationComplete,
  clientSecret,
  paymentIntentId,
  cardNumber
}) => {
  const toast = useRef<Toast>(null);

  const stripe = useStripe();
  const elements = useElements();

  const [loading, setLoading] = useState(false);
  const [saveBilling, setSaveBilling] = useState(false);

  const [billing, setBilling] = useState({
    name: '',
    country: '',
    address: '',
  });

  const navigate = useNavigate();
  const { t } = useTranslation();

  const [countries, setCountries] = useState<{ name: string; value: string }[]>(
    []
  );

  const [country, setCountry] = useState('');

  const [parentCardNumber, setParentCardNumber] = useState<string | null>(cardNumber);
  const [cardMismatch, setCardMismatch] = useState(false);

  const fetchPlanSupportedCountries = async () => {
    try {
      const response = (await getPlanSupportedCountries()) as AxiosResponse;

      const localCountries = response?.data?.data['country'];

      const formatted = localCountries.map((c: any) => ({
        label: c.display_title, // what user sees
        value: c.iso_code_alpha_2, // what you store
      }));

      setCountries(formatted);
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load countries.',
        life: 3000,
      });
    }
  };

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | DropdownChangeEvent
      | any
  ) => {
    const { name, value } = e.target || (e as DropdownChangeEvent);

    setCountry(value);
    setBilling((prev) => ({ ...prev, country: value }));
  };

  useEffect(() => {
    fetchPlanSupportedCountries();
  }, []);

  const handleVerify = async () => {
    if (!stripe || !elements) return;

    setLoading(true);

    try {
      const cardNumberElement = elements.getElement(CardNumberElement);
      if (!cardNumberElement) return;
      // Test Card Numbers
      // Successful payment - 4242424242424242
      // Mastercard (valid) - 5555555555554444
      // Card declined -4000000000000002
      // Requires authentication (3D Secure) - 4000002500003155

      // Create a token to get the last 4 digits for mismatch check
      // const cardNumberTyped = await stripe.createToken(cardNumberElement); // creates a token with number
      // const typedLast4 = cardNumberTyped.token?.card?.last4;

      // if (typedLast4 && parentCardNumber) {
      //   if (typedLast4 !== parentCardNumber.slice(-4)) {
      //     setCardMismatch(true);
      //     toast.current?.show({
      //       severity: 'warn',
      //       summary: 'Card Mismatch',
      //       detail: 'The card you typed does not match the expected card.',
      //     });
      //     return; // stop verification
      //   }
      // }

      // setCardMismatch(false);
      // **************************

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardNumberElement!,
          billing_details: saveBilling
            ? {
              name: billing.name,
              address: {
                line1: billing.address,
                country: billing.country,
              },
            }
            : undefined,
        },
      });

      if (result.error) {
        toast.current?.show({
          severity: 'error',
          summary: 'Verification Failed',
          detail: result.error.message,
        });
        setLoading(false);
        return;
      }

      if (result.paymentIntent?.status === 'succeeded' || result.paymentIntent?.status === 'requires_capture' || result.paymentIntent?.status === 'processing') {
        // await verifyCard(paymentIntentId);

        toast.current?.show({
          severity: 'success',
          summary: 'Verified',
          detail: 'Card verified successfully!',
        });

        setTimeout(() => {
          onClose();
        }, 500);
        setTimeout(() => {
          onVerificationComplete(true);
        }, 1000);
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.message,
      });
    }

    setLoading(false);
  };


  return (
    <>
      <Toast ref={toast} />

      <Dialog
        visible={visible}
        onHide={onClose}
        style={{ width: '35vw' }}
        breakpoints={{ '960px': '75vw', '640px': '95vw' }}
        dismissableMask
      >
        <h3 className="mb-3 text-center font-bold">
          {t('landing.esim_eligibility.verifying_card')}
        </h3>

        {parentCardNumber && (
          <div className="mb-2">
            <small>
              Using card ending with {parentCardNumber.slice(-4)}
            </small>
          </div>
        )}

        {/* FORM GRID */}
        <div className="grid p-2">
          {/* Card Number */}
          <div className="col-12">
            <label className="block mb-1">
              {t('landing.esim_eligibility.card_number')}
            </label>
            <div className="p-inputtext w-full p-2 border-1 border-round">
              <CardNumberElement options={elementStyle} />
            </div>
          </div>

          {/* Expiry */}
          <div className="col-6">
            <label className="block mb-1">
              {t('landing.esim_eligibility.expiration_date')}
            </label>
            <div className="p-inputtext w-full p-2 border-1 border-round">
              <CardExpiryElement options={elementStyle} />
            </div>
          </div>

          {/* CVC */}
          <div className="col-6">
            <label className="block mb-1">
              {t('landing.esim_eligibility.security_code')}
            </label>
            <div className="p-inputtext w-full p-2 border-1 border-round">
              <CardCvcElement options={elementStyle} />
            </div>
          </div>

          {/* Country */}
          <div className="col-12">
            <label className="block mb-1">
              {t('landing.esim_eligibility.country')}
            </label>
            {/* <InputText
              className="w-full"
              value={billing.country}
              onChange={(e) =>
                setBilling({ ...billing, country: e.target.value })
              }
            /> */}
            <Dropdown
              id="country"
              name="country"
              value={country}
              options={countries}
              onChange={handleChange}
              className="w-full"
              filter
              showClear
            />
          </div>

          {/* Checkbox */}
          <div className="col-12 flex align-items-center gap-2 hide">
            <Checkbox
              checked={saveBilling}
              onChange={(e) => setSaveBilling(e.checked!)}
            />
            <label>Add Billing Address</label>
          </div>

          {/* Billing Fields */}
          {saveBilling && (
            <>
              <div className="col-12">
                <InputText
                  placeholder="Full Name"
                  className="w-full"
                  onChange={(e) =>
                    setBilling({ ...billing, name: e.target.value })
                  }
                />
              </div>

              <div className="col-12">
                <InputText
                  placeholder="Address"
                  className="w-full"
                  onChange={(e) =>
                    setBilling({ ...billing, address: e.target.value })
                  }
                />
              </div>
            </>
          )}

          {/* Button */}

          {/* Note */}
          <div className="col-12 text-left mb-1 mt-1">
            <small>{t('landing.esim_eligibility.verifying_card_note')}</small>
          </div>
          <div className="col-12">
            <Button
              onClick={handleVerify}
              disabled={loading}
              className="w-full"
              label={t('landing.esim_eligibility.verify_card')}
            ></Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default VerifyingCardDialog;
